import type { CSSProperties, ReactElement } from 'react';
import { directionForSeed } from './controls/directionsData.js';
import { GAME_STAMP_COPY } from './messages/GameStamp.js';
import { EyeIcon, GAME_ROUND2_COPY } from './messages/GameRound2.js';
import type { GameSummary, GameSummaryMark } from './gameArena.js';
import { GamePractice } from './GamePractice.js';
import type { GamePracticeHost } from './gamePracticeItems.js';
import './gameArena.css';

/**
 * End-of-game summary (plan 47 Phase 4): inline in the thread, right after the
 * last message of a translate-it game the learner left (Salir, another seed)
 * or that the session ended in. Built by `gameSummaryOf` from the same stamps
 * the bubbles show — derived on every render, so an attempt still judging when
 * the game ended settles here too. A «No sé» (Round 2) is «vista»: its own
 * count, mark and review line with the revealed version.
 *
 * «Practicar estas N» (`GamePractice`) opens the debrief's correction review
 * over the persisted corrections of the game's attempts, pausing a live
 * session while it is open.
 */
interface GameSummaryCardProps {
  summary: GameSummary;
  /** «Practicar estas N»: the live session's id and its pause; absent = no practice entry (previews). */
  practice?: { sessionId: string | null | undefined; host: GamePracticeHost } | undefined;
}

/** Learner-facing copy. */
export const GAME_SUMMARY_COPY = {
  kicker: 'Fin de la partida',
  rounds: (n: number) => `${n} ${n === 1 ? 'ronda' : 'rondas'}`,
  clean: (n: number) => `${n} ${n === 1 ? 'limpia' : 'limpias'}`,
  close: (n: number) => `${n} casi`,
  miss: (n: number) => `${n} ${n === 1 ? 'errada' : 'erradas'}`,
  seen: (n: number) => `${n} ${n === 1 ? 'vista' : 'vistas'}`,
  bestStreak: (n: number) => `mejor racha ×${n}`,
  review: 'Para repasar',
  fixed: 'Mejor así',
  noFix: 'Sin forma corregida — el sentido no era ese.',
  allClean: 'Todas limpias. ¡Impecable!',
} as const;

export function GameSummaryCard({ summary, practice }: GameSummaryCardProps): ReactElement {
  const color = directionForSeed('translate-it')?.color;
  const { score } = summary;
  const rounds = Math.max(summary.rounds, 1);
  return (
    <section
      className="game-summary slide-up"
      style={color ? ({ '--dir-color': color } as CSSProperties) : undefined}
      aria-label={GAME_SUMMARY_COPY.kicker}
    >
      <div className="game-summary__kicker">
        {GAME_SUMMARY_COPY.kicker} · {directionForSeed('translate-it')?.chip ?? ''}
      </div>
      <div className="game-summary__title">{GAME_SUMMARY_COPY.rounds(rounds)}</div>
      <div className="game-summary__counts">
        <span className="game-summary__count is-clean">
          ✓ {GAME_SUMMARY_COPY.clean(score.clean)}
        </span>
        {score.close > 0 && (
          <span className="game-summary__count is-close">
            ≈ {GAME_SUMMARY_COPY.close(score.close)}
          </span>
        )}
        {score.miss > 0 && (
          <span className="game-summary__count is-miss">
            ✗ {GAME_SUMMARY_COPY.miss(score.miss)}
          </span>
        )}
        {score.seen > 0 && (
          <span className="game-summary__count is-seen">
            <EyeIcon size={12} />
            {GAME_SUMMARY_COPY.seen(score.seen)}
          </span>
        )}
        {score.bestStreak >= 2 && (
          <span className="game-summary__count">
            {GAME_SUMMARY_COPY.bestStreak(score.bestStreak)}
          </span>
        )}
      </div>
      <div className="game-summary__row" aria-hidden="true">
        {summary.marks.map((s, i) => (
          <SummaryDot key={i} stamp={s} />
        ))}
      </div>
      {summary.review.length > 0 || summary.revealed.length > 0 ? (
        <div className="game-summary__review">
          <div className="game-summary__review-title">{GAME_SUMMARY_COPY.review}</div>
          {summary.review.map((r) => (
            <div key={r.index} className={`game-summary__item is-${r.stamp}`}>
              {r.challenge && (
                <div className="game-summary__challenge" lang="es">
                  {r.challenge}
                </div>
              )}
              <div className={`game-summary__said${r.corrected ? '' : ' is-plain'}`}>
                <span className="game-sr-only">{GAME_STAMP_COPY[r.stamp].label}: </span>
                {r.said}
              </div>
              <div className="game-summary__fixed">
                {r.corrected ? (
                  <>
                    <span className="game-summary__fixed-label">{GAME_SUMMARY_COPY.fixed}</span>
                    {r.corrected}
                  </>
                ) : (
                  <span className="game-summary__nofix">{GAME_SUMMARY_COPY.noFix}</span>
                )}
              </div>
            </div>
          ))}
          {summary.revealed.map((r) => (
            <div key={`seen-${r.after}`} className="game-summary__item is-seen">
              <div className="game-summary__challenge" lang="es">
                <span className="game-sr-only">{GAME_ROUND2_COPY.seen}: </span>
                {r.challenge}
              </div>
              <div className="game-summary__fixed">
                {r.solution ? (
                  <>
                    <span className="game-summary__fixed-label is-seen">
                      {GAME_ROUND2_COPY.revealLead}
                    </span>
                    {r.solution}
                  </>
                ) : (
                  <span className="game-summary__nofix">{GAME_ROUND2_COPY.failed}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : score.attempts > 0 && score.clean === score.attempts ? (
        <div className="game-summary__empty">{GAME_SUMMARY_COPY.allClean}</div>
      ) : null}
      {practice && (
        <GamePractice review={summary.review} sessionId={practice.sessionId} host={practice.host} />
      )}
    </section>
  );
}

function SummaryDot({ stamp }: { stamp: GameSummaryMark }): ReactElement {
  if (stamp === 'seen') {
    // «Vista» is not a result: its own neutral mark, eye + text.
    return (
      <span className="game-summary__dot is-seen" title={GAME_ROUND2_COPY.seen}>
        <EyeIcon size={12} />
        <span className="game-summary__dot-text">{GAME_ROUND2_COPY.seen.toLowerCase()}</span>
      </span>
    );
  }
  const glyph = stamp === 'judging' ? '…' : stamp === 'close' ? '≈' : stamp === 'miss' ? '✗' : '✓';
  return (
    <span
      className={`game-summary__dot is-${stamp}`}
      title={stamp === 'judging' ? undefined : GAME_STAMP_COPY[stamp].label}
    >
      <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1 }}>{glyph}</span>
    </span>
  );
}
